// Host invite (/invite/:code): the link a host sends the day before a Tasted
// night. The page itself now lives on the website (apps/web, pages/Invite);
// what stays here is the redirect to it and the saved spot the join screen
// reads back. Plain functions with NO imports, so the redirect below can run
// from main.jsx before analytics or Sentry load.
//
// Nothing in this module touches Supabase, and nothing should. A game code is
// four characters and guessable; the invite page shows only what its own link
// carries, so it can never confirm that a code exists or who bought the box.

export const INVITE_STORAGE_KEY = 'tasted_invite_v1';
export const HOST_MAX = 24;
export const WHEN_MAX = 32;
// Matches the join screen's nickname field (maxLength 20), so a saved name
// is always one the player could have typed there.
export const NAME_MAX = 20;

/** Game codes are 4 characters (digits today). Anything else is not a code. */
export function normalizeInviteCode(raw) {
  const code = String(raw ?? '').trim().toUpperCase();
  return /^[A-Z0-9]{4}$/.test(code) ? code : '';
}

// Tags go first and whole, so `<b>Mel</b>` reads "Mel" rather than "bMelb".
// A lone "<" survives this; the per-field allowlists below remove it.
function stripTags(value) {
  return String(value ?? '').replace(/<[^>]*>/g, ' ');
}

function capAndTidy(value, max) {
  return value.replace(/\s+/g, ' ').trim().slice(0, max).trim();
}

/**
 * The host's first name: letters, spaces, apostrophes and hyphens only, max 24.
 * Unicode letters are kept, so "Zoë" and "Thandi-Lee" survive.
 */
export function sanitizeHost(raw) {
  const text = stripTags(raw).replace(/[^\p{L}\p{M} '’-]/gu, '');
  return capAndTidy(text, HOST_MAX);
}

/**
 * "Saturday 7pm", "Sat 4 Oct, 19:00". Plain text, max 32 chars: letters,
 * digits, spaces and everyday date punctuation. No angle brackets, quotes,
 * slashes-as-URLs or control characters.
 */
export function sanitizeWhen(raw) {
  const text = stripTags(raw).replace(/[^\p{L}\p{M}\p{N} '’:.,&()-]/gu, '');
  return capAndTidy(text, WHEN_MAX);
}

/** The guest's name for the saved spot. Same allowlist as the join screen's nickname filter plus apostrophes. */
export function sanitizeGuestName(raw) {
  const text = stripTags(raw).replace(/[^\p{L}\p{M}\p{N} '’-]/gu, '');
  return capAndTidy(text, NAME_MAX);
}

// Deliberately loose, like the join screen's: the form only has to catch typos
// such as a missing "@" or domain. The address never leaves the device here.
export function isPlausibleEmail(raw) {
  const value = String(raw ?? '').trim();
  return value.length <= 254 && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]{2,}$/.test(value);
}

// ── Saved spot (localStorage) ───────────────────────────────────────────────
// Only the latest invite is kept. It lives on this phone and nowhere else; the
// join screen reads it back to prefill the form on the night.

export function readSavedInvite() {
  try {
    const raw = window.localStorage?.getItem(INVITE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const code = normalizeInviteCode(parsed?.code);
    if (!code) return null;
    return {
      code,
      name: sanitizeGuestName(parsed.name),
      email: isPlausibleEmail(parsed.email) ? String(parsed.email).trim() : '',
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
    };
  } catch {
    return null;
  }
}

/** The saved invite, but only when it was saved for this game code. */
export function savedInviteForCode(rawCode) {
  const code = normalizeInviteCode(rawCode);
  if (!code) return null;
  const saved = readSavedInvite();
  return saved && saved.code === code ? saved : null;
}

/** Returns the stored record, or null when storage refused it. */
export function saveInvite({ code, name, email }) {
  const record = {
    code: normalizeInviteCode(code),
    name: sanitizeGuestName(name),
    email: String(email ?? '').trim(),
    savedAt: new Date().toISOString(),
  };
  if (!record.code) return null;
  try {
    window.localStorage.setItem(INVITE_STORAGE_KEY, JSON.stringify(record));
    return record;
  } catch {
    return null;
  }
}

// ── The invite lives on the website now ─────────────────────────────────────
// /invite/:code moved to tasted.co.za, where the tour is part of the invite
// and the link preview is served to chat apps. Links already sent point here,
// so this app forwards them, query string and all.
//
// It happens before analytics loads (lib/inviteScrub.js runs first in
// main.jsx): the host's name, and the date and time of their night, come off
// the address bar synchronously, and only the redirect target keeps them.

export const WEB_INVITE_ORIGIN = 'https://www.tasted.co.za';
const PRIVATE_PARAMS = ['host', 'when', 'date', 'time'];

/** The website's invite URL for a path segment and query string, verbatim. */
export function webInviteUrl(rawCode, search = '') {
  let segment = String(rawCode ?? '');
  try { segment = decodeURIComponent(segment); } catch { /* keep it as it came */ }
  const query = String(search || '');
  const qs = query && query !== '?' ? (query.startsWith('?') ? query : `?${query}`) : '';
  return `${WEB_INVITE_ORIGIN}/invite/${encodeURIComponent(segment)}${qs}`;
}

// Set by redirectInviteToWeb() at load, from the URL before it was scrubbed,
// so the route component can send the visitor to exactly the same place.
let pendingTarget = null;

/** Where the loaded /invite/ URL is headed, taken once. */
export function takeInviteRedirect() {
  const target = pendingTarget;
  pendingTarget = null;
  return target;
}

/** The page swap itself, split out so tests can watch it. */
export const inviteNav = {
  go(url) {
    window.location.replace(url);
  },
};

/**
 * On an /invite/ URL: note the website target (with the full original query),
 * take host/when/date/time off the address bar, then leave for the website.
 * Returns the target, or null on any other route.
 */
export function redirectInviteToWeb() {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const match = url.pathname.match(/^\/invite\/([^/]+)\/?$/);
    if (!match) return null;
    const target = webInviteUrl(match[1], url.search);
    pendingTarget = target;
    if (PRIVATE_PARAMS.some((p) => url.searchParams.has(p)) && window.history?.replaceState) {
      for (const p of PRIVATE_PARAMS) url.searchParams.delete(p);
      window.history.replaceState(window.history.state, '', url.toString());
    }
    inviteNav.go(target);
    return target;
  } catch {
    return null; /* a URL we cannot parse is one we cannot leak from either */
  }
}
